import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Sparkles, Trash2, Loader2 } from 'lucide-react';
import { EmptyState } from '@/components/ChartCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

export default function PromptsTab({ project }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [category, setCategory] = useState('general');
  const [adding, setAdding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [count, setCount] = useState(10);
  const [quotaError, setQuotaError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${project.id}/prompts`);
      setPrompts(r.data || []);
    } catch (_e) {
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setAdding(true);
    try {
      const r = await api.post(`/projects/${project.id}/prompts`, { text: text.trim(), category });
      setPrompts([r.data, ...prompts]);
      setText('');
      toast.success('Prompt added');
    } catch (_e) {
      toast.error('Failed to add prompt');
    } finally {
      setAdding(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setQuotaError('');
    try {
      const r = await api.post(`/projects/${project.id}/prompts/generate`, { count });
      setPrompts((previous) => [...(r.data || []), ...previous]);
      toast.success(`Generated ${r.data.length} prompts with AI`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        const message = typeof detail?.message === 'string'
          ? detail.message : 'Your plan does not allow this prompt request. Review your plan or choose a smaller count.';
        setQuotaError(message);
        // The shared API interceptor already shows the in-app Upgrade toast.
      } else if (status === 409 || status === 429) {
        toast.error('Another prompt request may be running. Please wait a moment and try again.');
      } else if (status === 422) {
        toast.error('Please choose a number between 3 and 25.');
      } else {
        toast.error('AI prompt generation failed. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${project.id}/prompts/${id}`);
      setPrompts(prompts.filter((p) => p.id !== id));
      toast.success('Prompt deleted');
    } catch (_e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border p-5">
        {quotaError && (
          <Alert variant="destructive" className="mb-4" data-testid="prompts-quota-alert">
            <AlertTitle data-testid="prompts-quota-alert-title">Prompt request exceeds your plan</AlertTitle>
            <AlertDescription data-testid="prompts-quota-alert-description">{quotaError}</AlertDescription>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" onClick={generate} disabled={generating || adding} data-testid="prompts-generate-retry-button">Try again</Button>
              <Button asChild data-testid="prompts-quota-upgrade-link"><Link to="/app/billing/upgrade">View plans</Link></Button>
            </div>
          </Alert>
        )}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-display text-base font-semibold">Add prompts</div>
            <p className="text-sm text-muted-foreground" data-testid="prompts-generation-help">Write a prompt manually, or generate 3–25 at once with AI within your plan’s remaining allowance.</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" min={3} max={25} value={count} onChange={(e) => setCount(Math.max(3, Math.min(25, Number(e.target.value) || 10)))} className="w-20" data-testid="prompts-generate-count-input" />
            <Button variant="outline" onClick={generate} disabled={generating} data-testid="prompts-generate-button">
              {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Generate with AI
            </Button>
          </div>
        </div>

        <form onSubmit={add} className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-3">
            <Label htmlFor="ptxt" className="sr-only">Prompt text</Label>
            <Textarea id="ptxt" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. What are the best note-taking apps for teams in 2025?" data-testid="prompts-add-text-input" />
          </div>
          <div className="flex flex-col gap-2">
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category" data-testid="prompts-add-category-input" />
            <Button type="submit" disabled={adding} data-testid="prompts-add-submit-button">
              {adding && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} <Plus className="h-4 w-4 mr-1.5" /> Add prompt
            </Button>
          </div>
        </form>
      </Card>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-semibold">Prompts</h3>
          <div className="text-xs text-muted-foreground">{prompts.length} total</div>
        </div>
        {loading ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div>
        ) : prompts.length === 0 ? (
          <Card className="border p-8">
            <EmptyState title="No prompts yet" description="Add prompts to run visibility scans." />
          </Card>
        ) : (
          <div className="space-y-2">
            {prompts.map((p) => (
              <Card key={p.id} className="border p-4" data-testid={`prompt-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm leading-relaxed">{p.text}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="text-[11px]">{p.category || 'general'}</Badge>
                      <Badge variant={p.source === 'ai_generated' ? 'default' : 'outline'} className="text-[11px]">
                        {p.source === 'ai_generated' ? 'AI-generated' : 'Manual'}
                      </Badge>
                    </div>
                  </div>
                  <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-rose-600" data-testid={`prompt-delete-${p.id}`} title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
